import { useMutation } from '@apollo/client'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import { InitializedRecord } from '@orbit/records'
import { RawDataGridTableSettings } from 'components/DataGrid/dataGridTableSettingsAdapter'
import {
  SavedReports,
  SavedReportsMenuActions,
} from 'components/SavedReports/SavedReports'
import { savedReportSchema } from 'components/SavedReports/yupSchemas'
import {
  DataGridSavedReportConfigInput,
  SavedReport,
} from 'generated/graphql'
import { useCurrentUser } from 'lib/CurrentUserProvider'
import { MouseEvent, useCallback, useState } from 'react'
import { CREATE_DATA_GRID_SAVED_REPORT_MUTATION, DESTROY_DATA_GRID_SAVED_REPORT_MUTATION, UPDATE_DATA_GRID_SAVED_REPORT_MUTATION } from 'components/DataGridSavedReports/mutations'
import { serializeDataGridSavedReportConfig } from 'components/DataGridSavedReports/dataGridSavedReportConfig'


export type SavedReportMenuProps = {
  anchorEl: HTMLElement | null
  onClose: () => void
  tableSettings: RawDataGridTableSettings
  updateTableSettings: (
    patch: Partial<RawDataGridTableSettings> | ((current: RawDataGridTableSettings) => Partial<RawDataGridTableSettings>)
  ) => void
  reports: SavedReport[]
  selectedReport: SavedReport | null
  hasChanges: () => boolean
  onSelectReport: (report: SavedReport) => void
  revertToSelectedSavedReport: () => void
  refetchReports: () => void
}

type LeadsSavedReportMenuProps = {
  anchorEl: HTMLElement | null
  onClose: () => void
  reports: SavedReport[]
  selectedReport: SavedReport | null
  onSelectReport: (report: SavedReport) => void
  actions: SavedReportsMenuActions
}

function LeadsSavedReportMenu({
  anchorEl,
  onClose,
  reports,
  selectedReport,
  onSelectReport,
  actions,
}: LeadsSavedReportMenuProps) {
  const [savedReportsAnchorEl, setSavedReportsAnchorEl] = useState<HTMLElement | null>(null)

  const handleOpenSavedReports = useCallback((event: MouseEvent<HTMLElement>) => {
    if (savedReportsAnchorEl !== event.currentTarget) {
      setSavedReportsAnchorEl(event.currentTarget)
    }
  }, [savedReportsAnchorEl])

  const handleCloseSavedReports = useCallback(() => {
    setSavedReportsAnchorEl(null)
  }, [])

  const handleClose = useCallback(() => {
    setSavedReportsAnchorEl(null)
    onClose()
  }, [onClose])

  const handleSelectReport = useCallback((report: SavedReport) => {
    setSavedReportsAnchorEl(null)
    onClose()
    onSelectReport(report)
  }, [onClose, onSelectReport])

  const validSavedReportsAnchorEl =
    savedReportsAnchorEl?.isConnected ? savedReportsAnchorEl : null

  return(
    <Menu
      id='saved-report-menu'
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={handleClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      PaperProps={{
        sx: { width: 240, textOverflow: 'ellipsis', overflowX: 'hidden', overflowY: 'auto', whiteSpace: 'nowrap' },
      }}
    >
      <MenuItem
        onMouseOver={handleOpenSavedReports}
        onMouseLeave={handleCloseSavedReports}
      >
        Saved Reports
        <ChevronRightIcon sx={{ ml: 'auto' }} />

        <Menu
          id='saved-reports-submenu'
          style={{ pointerEvents: 'none' }}
          anchorEl={validSavedReportsAnchorEl}
          open={Boolean(validSavedReportsAnchorEl)}
          anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          slotProps={{
            paper: {
              sx: { mt: '-8px', width: 240, maxHeight: 400, textOverflow: 'ellipsis', overflowX: 'hidden', overflowY: 'auto', whiteSpace: 'nowrap' },
            }
          }}
        >
          <div style={{ pointerEvents: 'auto' }}>
            {reports.length === 0 &&
              <MenuItem disabled>No Saved Reports</MenuItem>
            }

            {reports.map(report => (
              <MenuItem
                key={report.id}
                selected={report.id === selectedReport?.id}
                onClick={() => handleSelectReport(report)}
              >
                {report.name}
              </MenuItem>
            ))}
          </div>
        </Menu>
      </MenuItem>

      <MenuItem onClick={actions.openSaveNewReport} aria-label='Save as New Report'>Save as New Report</MenuItem>
      <MenuItem onClick={actions.openSaveReport} disabled={!selectedReport || !actions.savedReportHasChanges} aria-label='Save Report'>Save Report</MenuItem>
      <MenuItem onClick={actions.revertToSavedReport} disabled={!selectedReport || !actions.savedReportHasChanges}>Revert to Saved</MenuItem>
      <MenuItem onClick={actions.shareReport} disabled={!selectedReport}>Share</MenuItem>
      <MenuItem onClick={actions.openRenameReport} disabled={!selectedReport}>Rename</MenuItem>
      <MenuItem onClick={actions.openDeleteReport} disabled={!selectedReport}>Delete</MenuItem>
    </Menu>
  )
}

export function SavedReportMenu({
  anchorEl,
  onClose,
  tableSettings,
  updateTableSettings,
  reports,
  selectedReport,
  hasChanges,
  onSelectReport,
  revertToSelectedSavedReport,
  refetchReports,
}: SavedReportMenuProps) {
  const { currentUser } = useCurrentUser()

  const [gqlCreateReport] = useMutation(CREATE_DATA_GRID_SAVED_REPORT_MUTATION)
  const [gqlUpdateReport] = useMutation(UPDATE_DATA_GRID_SAVED_REPORT_MUTATION)
  const [gqlDeleteReport] = useMutation(DESTROY_DATA_GRID_SAVED_REPORT_MUTATION)

  const getReportConfig = useCallback(
    () => serializeDataGridSavedReportConfig(tableSettings),
    [tableSettings]
  )

  const createReport = useCallback(async ({
    name,
    reportConfig,
  }: {
    name: string
    reportConfig: DataGridSavedReportConfigInput
  }) => {
    const response = await gqlCreateReport({
      variables: {
        name,
        reportConfig,
        reportKey: 'leads',
        userId: (currentUser as InitializedRecord).id,
      },
    })

    const savedReport = response.data?.mutationResult.savedReport
    if(!savedReport) {
      throw new Error('Saved report was not returned after creation')
    }

    refetchReports()
    return savedReport
  }, [currentUser, gqlCreateReport, refetchReports])

  const updateReport = useCallback(async ({
    id,
    name,
    reportConfig,
  }: {
    id: string
    name?: string
    reportConfig?: DataGridSavedReportConfigInput
  }) => {
    const response = await gqlUpdateReport({
      variables: { id, name, reportConfig },
    })

    const savedReport = response.data?.mutationResult.savedReport
    if(!savedReport) {
      throw new Error('Saved report was not returned after update')
    }

    refetchReports()
    return savedReport
  }, [gqlUpdateReport, refetchReports])

  const deleteReport = useCallback(async (id: string) => {
    const response = await gqlDeleteReport({ variables: { id } })
    const savedReport = response.data?.mutationResult.savedReport

    if(!savedReport) {
      throw new Error('Saved report was not returned after deletion')
    }

    refetchReports()
    return savedReport
  }, [gqlDeleteReport, refetchReports])

  const updateSelectedReportIdentity = useCallback((savedReport: SavedReport) => {
    updateTableSettings({
      savedReportId: savedReport.id,
      savedReportName: savedReport.name,
    })
  }, [updateTableSettings])

  const handleReportDeleted = useCallback((_savedReport: SavedReport) => {
    updateTableSettings({
      savedReportId: null,
      savedReportName: null,
    })
  }, [updateTableSettings])

  return(
    <SavedReports
      selectedReport={selectedReport}
      initialName={selectedReport?.name ?? ''}
      getReportConfig={getReportConfig}
      createReport={createReport}
      updateReport={updateReport}
      deleteReport={deleteReport}
      savedReportValidationSchema={savedReportSchema}
      hasChanges={hasChanges}
      revertToSelectedSavedReport={revertToSelectedSavedReport}
      onReportCreated={updateSelectedReportIdentity}
      onReportUpdated={() => refetchReports()}
      onReportRenamed={updateSelectedReportIdentity}
      onReportDeleted={handleReportDeleted}
      onExport={() => {}}
      onMenuClose={onClose}
      renderMenu={actions => (
        <LeadsSavedReportMenu
          anchorEl={anchorEl}
          onClose={onClose}
          reports={reports}
          selectedReport={selectedReport}
          onSelectReport={onSelectReport}
          actions={actions}
        />
      )}
    />
  )
}
